import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import PermissionRoute from './components/PermissionRoute.jsx';
import './App.scss';

// Pages and layouts are generated per the design reference; lazily-imported
// and wired below.
const LoginPage = lazy(() => import('./pages/login/index.jsx').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/register/index.jsx').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/forgot-password/index.jsx').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('./pages/reset-password/index.jsx').then((m) => ({ default: m.ResetPasswordPage }))
);
const DashboardPage = lazy(() => import('./pages/dashboard/index.jsx'));
const ChangePasswordPage = lazy(() => import('./pages/change-password/index.jsx'));
const RoleListPage = lazy(() => import('./pages/role-list/index.jsx'));
const RolePermissionsPage = lazy(() => import('./pages/role-permissions/index.jsx'));
const UserListPage = lazy(() => import('./pages/user-list/index.jsx'));
const FacilityRegistryPage = lazy(() => import('./pages/facility-registry/index.jsx'));
const CoverageAndRosterManagementPage = lazy(() => import('./pages/coverage-and-roster-management/index.jsx'));
const CapacityDeclarationAndFeedsPage = lazy(() => import('./pages/capacity-declaration-and-feeds/index.jsx'));
const NetworkCapacityHeatmapPage = lazy(() => import('./pages/network-capacity-heatmap/index.jsx'));
const TransferIntakeFormPage = lazy(() => import('./pages/transfer-intake-form/index.jsx'));
const SbarHandoffWorkspacePage = lazy(() => import('./pages/sbar-handoff-workspace/index.jsx'));
const CoordinatorWorkstationPage = lazy(() => import('./pages/coordinator-workstation/index.jsx'));
const ClinicianDecisionPortalPage = lazy(() => import('./pages/clinician-decision-portal/index.jsx'));
const NetworkCommandWallboardPage = lazy(() => import('./pages/network-command-wallboard/index.jsx'));
const LedgerExplorerPage = lazy(() => import('./pages/ledger-explorer/index.jsx'));
const TransferPerformanceAndAnalyticsPage = lazy(() => import('./pages/transfer-performance-and-analytics/index.jsx'));
const IntegrationsPage = lazy(() => import('./pages/integrations/index.jsx'));

// Generic status pages — not part of the raw design; standard chrome every
// app needs. 404 is the router catch-all and the fallback target for any
// menuData.js link whose real page isn't built yet
// (create-frontend-layout-components Policy 10).
const NotFound = lazy(() => import('./pages/status/NotFound.jsx'));
const Unauthorized = lazy(() => import('./pages/status/Unauthorized.jsx'));
const Forbidden = lazy(() => import('./pages/status/Forbidden.jsx'));
const ServerError = lazy(() => import('./pages/status/ServerError.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public — must be reachable before any auth guard, so ProtectedRoute's
                redirect-to-/login always resolves to a real page. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Every page-scoped route below is wrapped in <PermissionRoute page="…" />,
                which asks the SERVER what this session may open (GET /api/auth/me/permissions,
                the same role_permissions rows requirePermission enforces) instead of consulting
                a static role table. An administrator ticking or unticking a box on the Role
                Permissions page now changes the client's navigation too, on the next load.
                `page` is the page slug from backend pageCatalog.js — identical to the route
                path, which is why no lookup table is needed here at all. */}
            <Route element={<ProtectedRoute />}>
              {/* Landing page for every authenticated role — the role-aware
                  work-queue dashboard (GET /api/dashboard decides the tiles).
                  Genuinely ungated server-side too, so no PermissionRoute. */}
              <Route path="/" element={<DashboardPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />

              {/* Access Management. Who holds these is a matrix question now, not a
                  role-name one — Network Ops Admin and Admin hold them in the seeded
                  matrix, and an administrator may change that. */}
              <Route element={<PermissionRoute page="role-list" />}>
                <Route path="/role-list" element={<RoleListPage />} />
              </Route>
              <Route element={<PermissionRoute page="role-permissions" />}>
                <Route path="/role-permissions" element={<RolePermissionsPage />} />
              </Route>
              <Route element={<PermissionRoute page="user-list" />}>
                <Route path="/user-list" element={<UserListPage />} />
              </Route>

              {/* Concern 39d929b5 — "Configure multi-EMR read-only integration settings":
                  Network Ops Admin (primary) + admin (full) edit; Auditor is view-only
                  (enforced inside the page, same pattern as Ledger Explorer's read-only roles). */}
              <Route element={<PermissionRoute page="integrations" />}>
                <Route path="/integrations" element={<IntegrationsPage />} />
              </Route>

              <Route element={<PermissionRoute page="facility-registry" />}>
                <Route path="/facility-registry" element={<FacilityRegistryPage />} />
              </Route>

              <Route element={<PermissionRoute page="coverage-and-roster-management" />}>
                <Route path="/coverage-and-roster-management" element={<CoverageAndRosterManagementPage />} />
              </Route>

              <Route element={<PermissionRoute page="capacity-declaration-and-feeds" />}>
                <Route path="/capacity-declaration-and-feeds" element={<CapacityDeclarationAndFeedsPage />} />
              </Route>

              <Route element={<PermissionRoute page="network-capacity-heatmap" />}>
                <Route path="/network-capacity-heatmap" element={<NetworkCapacityHeatmapPage />} />
              </Route>

              <Route element={<PermissionRoute page="transfer-intake-form" />}>
                <Route path="/transfer-intake-form" element={<TransferIntakeFormPage />} />
              </Route>

              <Route element={<PermissionRoute page="sbar-handoff-workspace" />}>
                <Route path="/sbar-handoff-workspace" element={<SbarHandoffWorkspacePage />} />
              </Route>

              <Route element={<PermissionRoute page="coordinator-workstation" />}>
                <Route path="/coordinator-workstation" element={<CoordinatorWorkstationPage />} />
              </Route>

              <Route element={<PermissionRoute page="clinician-decision-portal" />}>
                <Route path="/clinician-decision-portal" element={<ClinicianDecisionPortalPage />} />
              </Route>

              <Route element={<PermissionRoute page="network-command-wallboard" />}>
                <Route path="/network-command-wallboard" element={<NetworkCommandWallboardPage />} />
              </Route>

              <Route element={<PermissionRoute page="ledger-explorer" />}>
                <Route path="/ledger-explorer" element={<LedgerExplorerPage />} />
              </Route>

              <Route element={<PermissionRoute page="transfer-performance-and-analytics" />}>
                <Route path="/transfer-performance-and-analytics" element={<TransferPerformanceAndAnalyticsPage />} />
              </Route>
            </Route>

            {/* Status pages */}
            <Route path="/401" element={<Unauthorized />} />
            <Route path="/403" element={<Forbidden />} />
            <Route path="/500" element={<ServerError />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
